import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaPrint, FaArrowLeft } from 'react-icons/fa';

const InterestCalculationSheetDetail = () => {
  const { salesContractId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [sheetData, setSheetData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dnReportData, setDnReportData] = useState([]);

  const fetchSheetData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/report/interest-calculation-sheet/${salesContractId}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Merge navigation state data if available (for commodity_name fallback)
        const contractData = {
          ...data.contract,
          // Use navigation state as fallback if backend doesn't have commodity_name
          commodity_name: data.contract?.commodity_name || location.state?.commodity_name || null,
          buyer_name: data.contract?.buyer_name || location.state?.buyer_name || data.contract?.buyer_name
        };
        setContract(contractData);
        setSheetData(data.data);
        setSummary(data.summary || null);
        setDnReportData(data.dn_report_data || []);
        
        // Group data by month
        const grouped = groupDataByMonth(data.data);
        setGroupedData(grouped);
      } else {
        toast.error(data.error || 'Failed to fetch sheet data');
      }
    } catch (error) {
      console.error('Error fetching sheet data:', error);
      toast.error('Server Error');
    }
    setLoading(false);
  }, [salesContractId, location.state]);

  useEffect(() => {
    fetchSheetData();
  }, [fetchSheetData]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate('/report/interest-calculation-sheet');
  };

  // Group data by month
  const groupDataByMonth = (data) => {
    const groups = [];
    let currentMonth = null;
    let currentGroup = null;
    let previousMonthTotal = {
      adv_amount: 0,
      do_quantity: 0,
      sales_quantity: 0,
      tds_amount: 0,
      amount_received: 0,
      net_payment: 0,
      cumulative: 0,
      hc_cost: 0,
      storage_warehouse_charges: 0,
      due_amount_after_due_date: 0
    };

    data.forEach((row) => {
      if (currentMonth !== row.month_year) {
        // New month - create new group
        if (currentGroup) {
          // Calculate total for previous month (subtotal + previous total)
          currentGroup.total = {
            adv_amount: currentGroup.subtotal.adv_amount + previousMonthTotal.adv_amount,
            do_quantity: currentGroup.subtotal.do_quantity + previousMonthTotal.do_quantity,
            sales_quantity: currentGroup.subtotal.sales_quantity + previousMonthTotal.sales_quantity,
            tds_amount: currentGroup.subtotal.tds_amount + previousMonthTotal.tds_amount,
            amount_received: currentGroup.subtotal.amount_received + previousMonthTotal.amount_received,
            net_payment: currentGroup.subtotal.net_payment + previousMonthTotal.net_payment,
            cumulative: currentGroup.subtotal.cumulative + previousMonthTotal.cumulative, // Cumulative outstanding (subtotal + previous month total)
            hc_cost: currentGroup.subtotal.hc_cost + previousMonthTotal.hc_cost, // Sum of all daily interest costs
            storage_warehouse_charges: currentGroup.subtotal.storage_warehouse_charges + previousMonthTotal.storage_warehouse_charges, // Cumulative storage warehouse charges
            due_amount_after_due_date: currentGroup.subtotal.due_amount_after_due_date + previousMonthTotal.due_amount_after_due_date // Cumulative due amount
          };
          // Update previous month total for next month
          previousMonthTotal = { ...currentGroup.total };
          groups.push(currentGroup);
        }
        currentMonth = row.month_year;
        currentGroup = {
          month_year: row.month_year,
          month_name: row.month_name,
          rows: [],
          subtotal: {
            adv_amount: 0,
            do_quantity: 0,
            sales_quantity: 0,
            tds_amount: 0,
            amount_received: 0,
            net_payment: 0,
            cumulative: 0,
            hc_cost: 0,
            storage_warehouse_charges: 0,
            due_amount_after_due_date: 0
          },
          total: null
        };
      }
      currentGroup.rows.push(row);
      // Update subtotals (current month only)
      currentGroup.subtotal.adv_amount += parseFloat(row.adv_amount || 0);
      currentGroup.subtotal.do_quantity += parseFloat(row.do_quantity || 0);
      currentGroup.subtotal.sales_quantity += parseFloat(row.sales_quantity || 0);
      currentGroup.subtotal.tds_amount += parseFloat(row.tds_amount || 0);
      currentGroup.subtotal.amount_received += parseFloat(row.amount_received || 0);
      currentGroup.subtotal.net_payment += parseFloat(row.net_payment || 0);
      // Sum all daily interest costs for the month
      currentGroup.subtotal.hc_cost += parseFloat(row.hc_cost || 0);
      // Sum storage warehouse charges for the month
      currentGroup.subtotal.storage_warehouse_charges += parseFloat(row.storage_warehouse_charges || 0);
      // Sum due amount after due date for the month
      currentGroup.subtotal.due_amount_after_due_date += parseFloat(row.due_amount_after_due_date || 0);
      // Use last row's cumulative for month end
      currentGroup.subtotal.cumulative = parseFloat(row.cumulative || 0);
    });

    // Add last group
    if (currentGroup) {
      // Calculate total for last month
      currentGroup.total = {
        adv_amount: currentGroup.subtotal.adv_amount + previousMonthTotal.adv_amount,
        do_quantity: currentGroup.subtotal.do_quantity + previousMonthTotal.do_quantity,
        sales_quantity: currentGroup.subtotal.sales_quantity + previousMonthTotal.sales_quantity,
        tds_amount: currentGroup.subtotal.tds_amount + previousMonthTotal.tds_amount,
        amount_received: currentGroup.subtotal.amount_received + previousMonthTotal.amount_received,
        net_payment: currentGroup.subtotal.net_payment + previousMonthTotal.net_payment,
        cumulative: currentGroup.subtotal.cumulative + previousMonthTotal.cumulative, // Cumulative outstanding (subtotal + previous month total)
        hc_cost: currentGroup.subtotal.hc_cost + previousMonthTotal.hc_cost, // Sum of all daily interest costs
        storage_warehouse_charges: currentGroup.subtotal.storage_warehouse_charges + previousMonthTotal.storage_warehouse_charges, // Cumulative storage warehouse charges
        due_amount_after_due_date: currentGroup.subtotal.due_amount_after_due_date + previousMonthTotal.due_amount_after_due_date // Cumulative due amount
      };
      groups.push(currentGroup);
    }

    return groups;
  };

  if (loading) {
    return (
      <div className="bg-white shadow-md p-6">
        <div className="text-center p-8 text-gray-500">
          Loading sheet data...
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="bg-white shadow-md p-6">
        <div className="text-center p-8 text-red-500">
          Contract not found
        </div>
        <button
          onClick={handleBack}
          className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          @page {
            size: A4 landscape;
            margin: 5mm;
          }
          
          body {
            margin: 0;
            padding: 0;
            font-family: Arial, sans-serif;
            font-size: 10px;
            line-height: 1.3;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .page-header {
            text-align: center;
            margin-bottom: 12px;
            page-break-after: avoid;
          }
          
          .page-header h1 {
            font-size: 18px;
            font-weight: bold;
            text-transform: uppercase;
            margin: 0 0 4px 0;
          }
          
          .page-header h2 {
            font-size: 16px;
            font-weight: 600;
            margin: 0 0 4px 0;
          }
          
          .month-section {
            page-break-inside: avoid;
            page-break-after: always;
            margin-bottom: 0;
            margin-top: 20px;
          }
          
          .month-section:first-child {
            margin-top: 0;
          }
          
          .month-section:last-child {
            page-break-after: avoid;
          }
          
          .month-title {
            font-size: 18px;
            font-weight: 600;
            text-align: center;
            margin: 12px 0 6px 0;
            page-break-after: avoid;
          }
          
          .month-subtitle {
            font-size: 16px;
            font-weight: 600;
            text-align: center;
            margin: 0 0 8px 0;
            page-break-after: avoid;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            page-break-inside: avoid;
            font-size: 9px;
            table-layout: fixed;
          }
          
          th, td {
            border: 1px solid #000;
            padding: 4px 2px;
            text-align: left;
            vertical-align: middle;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          
          th {
            background-color: #f0f0f0;
            font-weight: 600;
            font-size: 8px;
          }
          
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          .text-left { text-align: left; }
          .bg-gray-100 { background-color: #f7fafc; }
          .bg-white { background-color: white; }
          .bg-gray-200 { background-color: #edf2f7; }
          .bg-gray-300 { background-color: #e2e8f0; }
          .font-semibold { font-weight: 600; }
          .font-bold { font-weight: 700; }
          .uppercase { text-transform: uppercase; }
          
          .month-section { 
            page-break-inside: avoid; 
            page-break-after: always; 
          }
          
          .month-section:last-child { 
            page-break-after: avoid; 
          }
          
          table { 
            page-break-inside: avoid;
            width: 100%;
          }
          
          tr { 
            page-break-inside: avoid; 
          }
          
          thead { 
            display: table-header-group; 
          }
          
          tfoot { 
            display: table-footer-group; 
          }
          
          .bg-blue-100 {
            background-color: #dbeafe !important;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .bg-blue-50 {
            background-color: #eff6ff !important;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .bg-gray-50 {
            background-color: #f9fafb !important;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      `}</style>

      <div className="p-4">
        {/* Header - Centered like example */}
        <div className="text-center mb-4 page-header">
          <h1 className="text-xl font-bold uppercase">M/s {contract?.buyer_name || contract?.buyer || 'N/A'}</h1>
          
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end mt-4 mb-4 no-print">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition mr-2"
          >
            <FaArrowLeft />
            Back
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-[#3899cf] text-white rounded hover:bg-[#0e8dc6] font-medium"
          >
            <FaPrint />
            Print
          </button>
        </div>

        {/* Month-wise Sections */}
        {groupedData.length > 0 ? (
          groupedData.map((monthGroup, groupIndex) => (
            <div key={monthGroup.month_year} className="mb-16 month-section">
              {/* Month Title and Subtitle */}
              <h3 className="text-xl font-semibold mb-1 mt-3 text-center month-title">
                {contract?.commodity_name || 'N/A'} ({monthGroup.month_name || 'N/A'})
              </h3>
              <p className="text-lg font-semibold  text-center month-subtitle">
                Calculation of Interest
              </p>
              
              {/* Contract Details Table - Show only in first month section */}
              {groupIndex === 0 && contract && (
                <div className="mb-4 overflow-x-auto">
                  <table className="min-w-full text-sm border border-gray-200 rounded mb-4">
                    <thead className="bg-blue-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-gray-700 font-semibold border border-gray-300">Contract No</th>
                        <th className="px-4 py-2 text-left text-gray-700 font-semibold border border-gray-300">Date</th>
                        <th className="px-4 py-2 text-left text-gray-700 font-semibold border border-gray-300">Name of buyer</th>
                        <th className="px-4 py-2 text-left text-gray-700 font-semibold border border-gray-300">Commodity</th>
                        <th className="px-4 py-2 text-center text-gray-700 font-semibold border border-gray-300">Qty</th>
                        <th className="px-4 py-2 text-center text-gray-700 font-semibold border border-gray-300">Rate</th>
                        <th className="px-4 py-2 text-center text-gray-700 font-semibold border border-gray-300">Total Amount</th>
                        <th className="px-4 py-2 text-center text-gray-700 font-semibold border border-gray-300">Due Date</th>
                        <th className="px-4 py-2 text-center text-gray-700 font-semibold border border-gray-300">Closing Date</th>
                        <th className="px-4 py-2 text-left text-gray-700 font-semibold border border-gray-300">DN of Account</th>
                        <th className="px-4 py-2 text-center text-gray-700 font-semibold border border-gray-300">DN Date</th>
                        <th className="px-4 py-2 text-center text-gray-700 font-semibold border border-gray-300">DN Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white hover:bg-blue-50">
                        <td className="px-4 py-2 border border-gray-300 text-gray-800 font-medium">
                          {contract?.sales_contract_no || 'N/A'}
                        </td>
                        <td className="px-4 py-2 border border-gray-300 text-gray-700">
                          {contract?.contract_date_formatted || 
                           (contract?.contract_date ? new Date(contract.contract_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A')}
                        </td>
                        <td className="px-4 py-2 border border-gray-300 text-gray-700">
                          {contract?.buyer_name || 'N/A'}
                        </td>
                        <td className="px-4 py-2 border border-gray-300 text-gray-700">
                          {contract?.commodity_name || 'N/A'}
                        </td>
                        <td className="px-4 py-2 border border-gray-300 text-center text-gray-700">
                          {parseFloat(contract?.contract_quantity_mt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-2 border border-gray-300 text-center text-gray-700">
                          {parseFloat(contract?.sale_price_per_mt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-2 border border-gray-300 text-center text-gray-800 font-semibold">
                          {parseFloat(contract?.total_contract_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-2 border border-gray-300 text-center text-gray-700">
                          {contract?.delivery_end_date_formatted || 
                           (contract?.delivery_end_date ? new Date(contract.delivery_end_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A')}
                        </td>

                        <td className="px-4 py-2 border border-gray-300 text-center text-gray-700">
                          {contract?.closing_date_formatted || 
                           (contract?.closing_date ? new Date(contract.closing_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A')}
                        </td>

                        <td className="px-4 py-2 border border-gray-300 text-gray-700">
                          {contract?.debit_notes && contract.debit_notes.length > 0 ? (
                            <div className="space-y-1">
                              {contract.debit_notes.map((dn, idx) => (
                                <div key={idx}>{dn.dn_of_account || '-'}</div>
                              ))}
                            </div>
                          ) : 'N/A'}
                        </td>
                        <td className="px-4 py-2 border border-gray-300 text-center text-gray-700">
                          {contract?.debit_notes && contract.debit_notes.length > 0 ? (
                            <div className="space-y-1">
                              {contract.debit_notes.map((dn, idx) => (
                                <div key={idx}>
                                  {dn.dn_date_formatted || (dn.dn_date ? new Date(dn.dn_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-')}
                                </div>
                              ))}
                            </div>
                          ) : 'N/A'}
                        </td>
                        <td className="px-4 py-2 border border-gray-300 text-center text-gray-700">
                          {contract?.debit_notes && contract.debit_notes.length > 0 ? (
                            <div className="space-y-1">
                              {contract.debit_notes.map((dn, idx) => (
                                <div key={idx}>
                                  {dn.dn_amount ? parseFloat(dn.dn_amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                                </div>
                              ))}
                              {contract.debit_notes.length > 1 && (
                                <div className="border-t border-gray-300 pt-1 font-bold text-gray-900 mt-1">
                                  Total: {parseFloat(contract.dn_amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                              )}
                            </div>
                          ) : 'N/A'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Month Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs border border-gray-200 rounded">
                  <thead>
                    <tr className="bg-blue-100">
                      <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Date</th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Adv amount</th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">DO No</th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">DO Quantity</th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Lifted Quantity</th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Balance Quantity</th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Total Amount</th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Storage warehouse charges</th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">TDS Amount</th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Amount Received</th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Net Outstanding of the day</th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Cumulative Outstanding</th>
                      <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Interest 11%</th>
                    </tr>
                  </thead>
                  <tbody>
              
                    {/* Month Data Rows */}
                    {monthGroup.rows.map((row, index) => {
                      const rowClass = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';
                      return (
                        <tr key={`${monthGroup.month_year}-${index}`} className={`border border-gray-200 ${rowClass} hover:bg-blue-50 transition duration-200 ease-in-out`}>
                          <td className="border border-gray-200 px-2 py-1">{row.date_formatted}</td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                            {parseFloat(row.adv_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-left text-gray-700">
                            {row.do_number || '-'}
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                            {parseFloat(row.do_quantity || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                            {parseFloat(row.sales_quantity || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                            {parseFloat(row.balance_quantity || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-gray-700 font-semibold">
                            {parseFloat(row.due_amount_after_due_date || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                            {parseFloat(row.storage_warehouse_charges || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                            {parseFloat(row.tds_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                            {parseFloat(row.amount_received || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                            {parseFloat(row.net_payment || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                            {parseFloat(row.cumulative || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                            {Math.max(0, parseFloat(row.hc_cost || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Subtotal Row */}
                    <tr className="bg-gray-200 font-semibold">
                      <td className="border border-gray-200 px-2 py-1 text-gray-700">Subtotal</td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                        {monthGroup.subtotal.adv_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-left text-gray-800">-</td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                        {monthGroup.subtotal.do_quantity.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                        {monthGroup.subtotal.sales_quantity.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                        -
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-gray-800 font-semibold">
                        {monthGroup.rows
                          .reduce((sum, row) => sum + parseFloat(row.due_amount_after_due_date || 0), 0)
                          .toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                        {monthGroup.rows
                          .reduce((sum, row) => sum + parseFloat(row.storage_warehouse_charges || 0), 0)
                          .toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                        {monthGroup.subtotal.tds_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                        {monthGroup.subtotal.amount_received.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                        -
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                        -
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                        {Math.max(0, monthGroup.subtotal.hc_cost).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>

                    {/* Total Row */}
                    {monthGroup.total && (
                      <tr className="bg-gray-50 font-bold">
                        <td className="border border-gray-200 px-2 py-1 text-gray-700">Total</td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                          {monthGroup.total.adv_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-left text-gray-800">-</td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                          {monthGroup.total.do_quantity.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                          {monthGroup.total.sales_quantity.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                          -
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-800 font-semibold">
                          {monthGroup.total.due_amount_after_due_date.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                          {monthGroup.total.storage_warehouse_charges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                          {monthGroup.total.tds_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                          {monthGroup.total.amount_received.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                          -
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                          -
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                          {Math.max(0, monthGroup.total.hc_cost).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    )}

                    {/* Total of Storage warehouse charges Row */}
                    <tr className="bg-gray-100 font-semibold">
                      <td className="border border-gray-200 px-2 py-1 text-gray-700">Total of Storage warehouse charges</td>
                      <td colSpan="11" className="border border-gray-200 px-2 py-1"></td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                        {monthGroup.total ? monthGroup.total.storage_warehouse_charges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : monthGroup.subtotal.storage_warehouse_charges.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>

                    {/* Total storage + interest Row */}
                    <tr className="bg-gray-100 font-semibold">
                      <td className="border border-gray-200 px-2 py-1 text-gray-700">Total storage + interest</td>
                      <td colSpan="11" className="border border-gray-200 px-2 py-1"></td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                        {(
                          (monthGroup.total ? monthGroup.total.storage_warehouse_charges : monthGroup.subtotal.storage_warehouse_charges) +
                          Math.max(0, monthGroup.total ? monthGroup.total.hc_cost : monthGroup.subtotal.hc_cost)
                        ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ))
        ) : sheetData.length > 0 ? (
          <div className="mb-16 month-section">
            <h3 className="text-xl font-semibold mb-1 mt-3 text-center month-title">
              {contract?.commodity_name || 'N/A'}
            </h3>
            <p className="text-lg font-semibold mb-2 text-center month-subtitle">
              Calculation of Holding & Carrying Cost
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs border border-gray-200 rounded">
                <thead>
                  <tr className="bg-blue-100">
                    <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Date</th>
                    <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Adv amount</th>
                    <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">DO No</th>
                    <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">DO Quantity</th>
                    <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Lifted Quantity</th>
                    <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Balance Quantity</th>
                    <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Total Amount</th>
                    <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Storage warehouse charges</th>
                    <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">TDS Amount</th>
                    <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Amount Received</th>
                    <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Net Outstanding of the day</th>
                    <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">Cumulative Outstanding</th>
                    <th className="border border-gray-200 px-2 py-1 text-left text-sm font-semibold text-gray-700">H and C Cost. 11.5%</th>
                  </tr>
                </thead>
                <tbody>
                  {sheetData.map((row, index) => {
                    const rowClass = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';
                    return (
                      <tr key={index} className={`border border-gray-200 ${rowClass} hover:bg-blue-50 transition duration-200 ease-in-out`}>
                        <td className="border border-gray-200 px-2 py-1 text-gray-700">{row.date_formatted}</td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                          {parseFloat(row.adv_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-left text-gray-700">
                          {row.do_number || '-'}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                          {parseFloat(row.do_quantity || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                          {parseFloat(row.sales_quantity || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                          {parseFloat(row.balance_quantity || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-700 font-semibold">
                          {parseFloat(row.due_amount_after_due_date || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                          {parseFloat(row.storage_warehouse_charges || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                          {parseFloat(row.tds_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                          {parseFloat(row.amount_received || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                          {parseFloat(row.net_payment || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                          {parseFloat(row.cumulative || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right text-gray-700">
                          {Math.max(0, parseFloat(row.hc_cost || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Total of Storage warehouse charges Row */}
                  <tr className="bg-gray-100 font-semibold">
                    <td className="border border-gray-200 px-2 py-1 text-gray-700">Total of Storage warehouse charges</td>
                    <td colSpan="11" className="border border-gray-200 px-2 py-1"></td>
                    <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                      {sheetData
                        .reduce((sum, row) => sum + parseFloat(row.storage_warehouse_charges || 0), 0)
                        .toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>

                  {/* Total storage + interest Row */}
                  <tr className="bg-gray-100 font-semibold">
                    <td className="border border-gray-200 px-2 py-1 text-gray-700">Total storage + interest</td>
                    <td colSpan="11" className="border border-gray-200 px-2 py-1"></td>
                    <td className="border border-gray-200 px-2 py-1 text-right text-gray-800">
                      {(
                        sheetData.reduce((sum, row) => sum + parseFloat(row.storage_warehouse_charges || 0), 0) +
                        sheetData.reduce((sum, row) => sum + Math.max(0, parseFloat(row.hc_cost || 0)), 0)
                      ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center p-8 text-gray-500 italic">
            No data available for the selected period.
          </div>
        )}

        {/* Message when Sales Order Quantity is within ±10% of Contract Quantity */}
        {contract && summary && (() => {
          const contractQty = parseFloat(contract.contract_quantity_mt || 0);
          const salesOrderQty = parseFloat(summary.total_sales_quantity || 0);
          
          if (contractQty <= 0) return null;
          
          const lowerBound = contractQty * 0.90; // 10% less
          const upperBound = contractQty * 1.10; // 10% more
          const isWithinRange = salesOrderQty >= lowerBound && salesOrderQty <= upperBound;
          
          if (!isWithinRange) return null;
          
          return (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md text-center">
              <p className="text-green-800 font-semibold text-sm">
                ✓ Total Sales Order Quantity ({salesOrderQty.toFixed(3)} MT) is within ±10% of Contract Quantity ({contractQty.toFixed(3)} MT)
              </p>
            </div>
          );
        })()}

        {/* DN Details Report Section */}
        <div className="mt-8 overflow-x-auto">
          <table className="min-w-full text-xs border border-gray-200 rounded mb-4">
            <thead>
              <tr className="bg-blue-100">
                <th colSpan="7" className="px-4 py-2 text-center text-gray-800 font-bold border border-gray-300 uppercase">
                  Details Of DN
                </th>
              </tr>
              <tr className="bg-gray-100">
                <th className="px-4 py-2 text-left text-gray-700 font-semibold border border-gray-300" style={{ width: '50px' }}>Sl. No.</th>
                <th className="px-4 py-2 text-left text-gray-700 font-semibold border border-gray-300">DN of Account</th>
                <th className="px-4 py-2 text-center text-gray-700 font-semibold border border-gray-300" style={{ width: '100px' }}>DN Date</th>
                <th className="px-4 py-2 text-right text-gray-700 font-semibold border border-gray-300" style={{ width: '120px' }}>DN Amount</th>
                <th className="px-4 py-2 text-left text-gray-700 font-semibold border border-gray-300">DN of system</th>
                <th className="px-4 py-2 text-center text-gray-700 font-semibold border border-gray-300" style={{ width: '130px' }}>DN Date of System</th>
                <th className="px-4 py-2 text-right text-gray-700 font-semibold border border-gray-300" style={{ width: '140px' }}>Dn Amount of System</th>
              </tr>
            </thead>
            <tbody>
              {dnReportData.length > 0 ? (
                <>
                  {dnReportData.map((row, index) => (
                    <tr key={index} className="bg-white hover:bg-blue-50">
                      <td className="px-4 py-2 border border-gray-300 text-gray-700 text-left">{index + 1}</td>
                      <td className="px-4 py-2 border border-gray-300 text-gray-700 text-left">{row.dn_of_account || '-'}</td>
                      <td className="px-4 py-2 border border-gray-300 text-gray-700 text-center">{row.dn_date_formatted || '-'}</td>
                      <td className="px-4 py-2 border border-gray-300 text-gray-700 text-right">
                        {row.dn_amount ? parseFloat(row.dn_amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                      </td>
                      <td className="px-4 py-2 border border-gray-300 text-gray-700 text-left font-semibold">{row.system_dn_no || '-'}</td>
                      <td className="px-4 py-2 border border-gray-300 text-gray-700 text-center">{row.system_dn_date_formatted || '-'}</td>
                      <td className="px-4 py-2 border border-gray-300 text-gray-700 text-right font-semibold">
                        {row.system_dn_amount ? parseFloat(row.system_dn_amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-200 font-bold">
                    <td colSpan="3" className="px-4 py-2 border border-gray-300 text-center italic">
                      Total DN Amount
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-right font-bold">
                      {parseFloat(
                        dnReportData.reduce((sum, row) => sum + parseFloat(row.dn_amount || 0), 0)
                      ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td colSpan="2" className="px-4 py-2 border border-gray-300 text-center italic">
                      Total System DN Amount
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-right font-bold">
                      {parseFloat(
                        dnReportData.reduce((sum, row) => sum + parseFloat(row.system_dn_amount || 0), 0)
                      ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-4 border border-gray-300 text-center text-gray-500 italic">
                    No DN details found for this contract.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ToastContainer 
          position="top-right" 
          autoClose={3000} 
          hideProgressBar={false} 
          newestOnTop 
          closeOnClick 
          pauseOnFocusLoss 
          draggable 
          pauseOnHover 
          className="no-print"
        />
      </div>
    </div>
  );
};

export default InterestCalculationSheetDetail;

